import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';

// Parse the stored offset string ("x% y%", where 0% 0% = centered) into [x, y].
function parseOffset(position) {
  const m = String(position || '').match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  return [0, 0];
}

// Free drag-to-position + free zoom editor for the company logo.
// Movement is a transform translate offset (% of the logo box) and zoom is scale —
// both unconstrained, matching how the public templates render the logo.
export default function LogoAdjustModal({ open, onClose, src, scale = 1, position = '', onApply, title = 'Adjust logo', label = 'logo', fit = 'contain', aspect = '1 / 1' }) {
  // For photos (cover) the frame must always stay filled — zooming below 1 would
  // reveal empty space and look like the image was cropped. Logos (contain) may shrink.
  const minZoom = fit === 'cover' ? 1 : 0.2;
  const [off, setOff] = useState(parseOffset(position)); // [x%, y%]
  const [z, setZ] = useState(Math.max(minZoom, Number(scale) || 1));
  const boxRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (open) {
      setOff(parseOffset(position));
      setZ(Math.max(minZoom, Number(scale) || 1));
    }
  }, [open, position, scale, minZoom]);

  const onDown = (e) => {
    const rect = boxRef.current.getBoundingClientRect();
    drag.current = { x: e.clientX, y: e.clientY, ox: off[0], oy: off[1], w: rect.width, h: rect.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    // 1:1 tracking: the logo box width == image width, so px delta maps directly to % offset.
    const nx = d.ox + ((e.clientX - d.x) / d.w) * 100;
    const ny = d.oy + ((e.clientY - d.y) / d.h) * 100;
    setOff([nx, ny]);
  };
  const onUp = (e) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const offsetStr = `${Math.round(off[0])}% ${Math.round(off[1])}%`;
  const transform = `translate(${offsetStr.replace(' ', ', ')}) scale(${z})`;

  const apply = () => {
    onApply?.({ scale: z, position: offsetStr });
    onClose?.();
  };
  const reset = () => {
    setOff([0, 0]);
    setZ(minZoom > 1 ? minZoom : 1);
  };

  const checker = 'repeating-conic-gradient(#e5e7eb 0% 25%, #f8fafc 0% 50%) 50% / 16px 16px';
  // Photos are opaque and always fill the frame → show a neutral backdrop, not the
  // transparency checkerboard (which is only meaningful for logos with transparent areas).
  const previewBg = fit === 'cover' ? '#0f172a' : checker;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
          Drag the {label} to move it freely. Use the slider to zoom in and out.
        </Typography>
        <Box
          ref={boxRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 300,
            aspectRatio: aspect,
            mx: 'auto',
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            background: previewBg,
            touchAction: 'none',
            userSelect: 'none'
          }}
        >
          <Box
            component="img"
            src={src}
            alt={label}
            draggable={false}
            sx={{ width: '100%', height: '100%', objectFit: fit, transform, pointerEvents: 'none' }}
          />
          {/* rule-of-thirds grid overlay */}
          <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {[33.33, 66.66].map((p) => (
              <Box key={`v${p}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${p}%`, width: '1px', bgcolor: 'rgba(0,0,0,0.22)' }} />
            ))}
            {[33.33, 66.66].map((p) => (
              <Box key={`h${p}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${p}%`, height: '1px', bgcolor: 'rgba(0,0,0,0.22)' }} />
            ))}
            <Box sx={{ position: 'absolute', inset: 0, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 2 }} />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Zoom</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{z.toFixed(2)}×</Typography>
          </Box>
          <Slider value={z} min={minZoom} max={4} step={0.05} onChange={(_, v) => setZ(v)} size="small" />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={reset} color="inherit">Reset</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={apply} variant="contained">Apply</Button>
      </DialogActions>
    </Dialog>
  );
}
